import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { SelectedCompanyContext } from '../../../contexts/SelectedCompanyContext';
import api from '../../../api';
import { toast } from '../../../utils/toast';
import { AnimatedBackground } from '../../ui/modern';
import { useTaskTemplates } from '../../../hooks/queries/useTaskTemplates';
import { buildAllowedUserFieldKeysFromServices } from '../../../utils/statusMappings';

// Sub-components
import ProgressSummary from './ProgressSummary';
 
import BranchNotes from './BranchNotes';
import ServerSection from './ServerSection';
import { ModernCard } from '../../ui/modern';
import DynamicEntitySection from '../../DynamicForm/DynamicEntitySection';

/**
 * Onboarding Branch Details Component
 * Main orchestrator for branch onboarding workflow
 */
const OnboardingBranchDetails = ({ user = {} }) => {
  const { selected, setSelected } = useContext(SelectedCompanyContext);
  const { branchId } = useParams();
  
  // Fetch branch services from task templates
  const { data: templates, isLoading: templatesLoading } = useTaskTemplates(selected?.sectorId);
  const BRANCH_SERVICES = React.useMemo(() => {
    if (!templates || !Array.isArray(templates)) return [];
    return templates
      .filter((t) => t.group === 'branchServices' && t.isActive !== false)
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
      .map((t) => t.title);
  }, [templates]);

  const [loading, setLoading] = useState(true);
  const [branchInfo, setBranchInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  // Active services will be computed from tasks - start empty
  const [activeServices, setActiveServices] = useState([]);
  const [branchNotes, setBranchNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [savingService, setSavingService] = useState('');
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null);
  const [dynamicEntityTypes, setDynamicEntityTypes] = useState([]);

  const refreshTasks = useCallback(async () => {
    if (!selected) return;
    try {
      const res = await api.get(`/tasks/company/${selected.id}`);
      const branchTasks = res.data.filter((t) => Number(t.branchId) === Number(branchId));
      setTasks(branchTasks);
    } catch (err) {
      console.error('fetch tasks', err);
    }
  }, [branchId, selected]);

  useEffect(() => {
    async function loadEntityTypes() {
      if (!selected?.sectorId) return;
      try {
        const res = await api.get('/entity-types', {
          params: { sectorId: selected.sectorId, scope: 'branch' },
        });
        setDynamicEntityTypes(res.data || []);
      } catch (err) {
        console.error('fetch entity types', err);
      }
    }
    loadEntityTypes();
  }, [selected?.sectorId]);

  const currUser = user || {};
  const editableRole = currUser.role === 'Admin' || currUser.role === 'Manager';
  const canEditServices = currUser.role === 'Admin' || currUser.role === 'Manager';

  // Load branch data
  const loadBranchData = useCallback(async () => {
    if (!selected) return;

    const info = selected.Branches?.find((b) => b.id === Number(branchId));
    setBranchInfo(info);

    // Ensure onboarding placeholders match branch configuration
    if (info) {
      try {
        const onboard = await api.get(`/branches/${branchId}/onboarding`).then((r) => r.data);
        const desiredUsers = info.numberOfUsers || 0;
        const desiredDevices = (info.numberOfNetworkDevices || 0) + (info.numberOfDevices || 0);
        
        const diffUsers = desiredUsers - onboard.users.length;
        const diffDev = desiredDevices - onboard.devices.length;
        
        // Handle adding new rows or removing excess rows
        if (diffUsers !== 0 || diffDev !== 0) {
          // If we need to remove excess rows (diff is negative), delete them
          if (diffUsers < 0) {
            // Delete excess user rows (remove incomplete rows first, from the end)
            const toDelete = onboard.users
              .filter(u => !u.completed)
              .slice(diffUsers); // Get last N incomplete users to delete
            
            for (const user of toDelete) {
              try {
                await api.delete(`/branches/user/${user.id}`);
              } catch (e) {
                console.error('Failed to delete user:', e);
              }
            }
          }
          
          if (diffDev < 0) {
            // Delete excess device rows (remove incomplete rows first, from the end)
            const toDelete = onboard.devices
              .filter(d => !d.completed)
              .slice(diffDev); // Get last N incomplete devices to delete
            
            for (const device of toDelete) {
              try {
                await api.delete(`/branches/device/${device.id}`);
              } catch (e) {
                console.error('Failed to delete device:', e);
              }
            }
          }
          
          // If we need to add new rows (diff is positive), create them
          if (diffUsers > 0 || diffDev > 0) {
            await api.post(`/branches/${branchId}/onboarding/init`, {
              usersCount: Math.max(diffUsers, 0),
              devicesCount: Math.max(diffDev, 0),
            });
          }
          
          // Refresh after changes
          const refreshed = await api.get(`/branches/${branchId}/onboarding`).then((r) => r.data);
          setUsers(refreshed.users);
          setDevices(refreshed.devices);
        } else {
          setUsers(onboard.users);
          setDevices(onboard.devices);
        }
      } catch (err) {
        console.error('onboard fetch', err);
      }
    }

    // Fetch branch notes
    try {
      const notesRes = await api.get(`/branch-notes/${branchId}/notes`);
      setBranchNotes(notesRes.data);
    } catch (e) {
      console.error('fetch branch notes', e);
    }

    setLoading(false);
    await refreshTasks();

  }, [branchId, selected, refreshTasks]);

  useEffect(() => {
    loadBranchData();
  }, [loadBranchData]);

  // Compute active services from tasks
  useEffect(() => {
    let source = tasks;
    if (source.length === 0 && selected?.CompanyTasks) {
      source = selected.CompanyTasks.filter((t) => Number(t.branchId) === Number(branchId));
    }
    const normalize = (str) => str.replace(/^(Install|Setup)\s+/i, '').trim().toLowerCase();
    const typeSet = new Set(source.map((t) => normalize(t.type)));
    const detected = BRANCH_SERVICES.filter((s) => typeSet.has(normalize(s)));
    setActiveServices(detected);
  }, [tasks, selected?.CompanyTasks, branchId, BRANCH_SERVICES]);

  const allowedUserQuickFieldKeys = React.useMemo(() => {
    return buildAllowedUserFieldKeysFromServices(activeServices);
  }, [activeServices]);

  // Update user field
  const handleUpdateUser = async (userRow, changes) => {
    try {
      const res = await api.put(`/branches/user/${userRow.id}`, changes);
      const patched = { ...res.data };
      if (changes.completed) {
        patched.completedBy = currUser.name || currUser.username || '';
      }
      setUsers((prev) => prev.map((u) => (u.id === userRow.id ? patched : u)));
    } catch (e) {
      console.error('update user', e);
      toast.error('Failed to update user');
    }
  };

  // Save user (mark complete)
  const handleSaveUser = (user) => {
    if (user.completed) return;
    if (!user.userName?.trim()) {
      toast.error("Please enter the user's name first");
      return;
    }
    handleUpdateUser(user, { completed: true, completedAt: new Date() });
  };

  // Update device field (local only, no API to avoid rate limit)
  const handleUpdateDevice = (devRow, changes) => {
    setDevices((prev) =>
      prev.map((d) =>
        d.id === devRow.id
          ? { ...d, ...changes, updatedLocally: true }
          : d
      )
    );
  };

  // Save device (mark complete)
  const handleSaveDevice = async (device) => {
    if (device.completed) return;
    if (!device.deviceName || !device.ipAddress) {
      toast.error('Name and IP required');
      return;
    }
    const agentInstalledStatus = device.agentInstalledStatus || (device.agentInstalled ? 'Yes' : 'No');
    const payload = {
      deviceName: device.deviceName,
      ipAddress: device.ipAddress,
      deviceUsername: device.deviceUsername,
      devicePassword: device.devicePassword,
      agentInstalledStatus,
      // legacy boolean kept in sync for older codepaths
      agentInstalled: agentInstalledStatus === 'Yes',
      notes: device.notes || '',
      completed: true,
      completedAt: new Date(),
      completedBy: currUser.name || currUser.username || '',
    };
    try {
      const res = await api.put(`/branches/device/${device.id}`, payload);
      const patched = { ...res.data };
      setDevices((prev) => prev.map((d) => (d.id === device.id ? patched : d)));
      toast.success('Device saved');
    } catch (e) {
      console.error('update device', e);
      toast.error('Failed to update device');
    }
  };

  // Update branch field
  const handleUpdateBranch = async (changes) => {
    try {
      const res = await api.put(`/branches/${branchId}`, changes);
      setBranchInfo(res.data);
      setSelected((prev) => ({
        ...prev,
        Branches: prev.Branches.map((b) => (b.id === res.data.id ? res.data : b)),
      }));
    } catch (e) {
      console.error('update branch', e);
      toast.error('Failed to update branch');
    }
  };

  // Save firewall
  const handleSaveFirewall = () => {
    if (branchInfo.firewallCompleted) return;
    
    const required = ['firewallName', 'firewallBrand', 'firewallIP', 'firewallUsername', 'firewallPassword'];
    for (const f of required) {
      if (!branchInfo[f]) {
        toast.error('Please fill all required firewall fields');
        return;
      }
    }
    if (!branchInfo.firewallAgentInstalled) {
      toast.error('Mark agent installed');
      return;
    }
    if (!branchInfo.oldAdminRemoved || branchInfo.oldAdminRemoved === 'No') {
      toast.error('Select old admin removed status');
      return;
    }
    
    handleUpdateBranch({ firewallCompleted: true, firewallCompletedAt: new Date() });
    toast.success('Firewall saved successfully');
  };

  // Add branch note
  const handleAddNote = async (noteText) => {
    try {
      const res = await api.post(`/branch-notes/${branchId}/notes`, { note: noteText });
      setBranchNotes([...branchNotes, res.data]);
    } catch (e) {
      console.error(e);
      toast.error('Failed to add note');
      throw e;
    }
  };

  const handleToggleService = async (service, enable) => {
    if (!canEditServices || !isEditingServices) {
      return; // No-op if not in edit mode or insufficient permissions
    }

    if (enable) {
      // Add service
      const exists = tasks.some((t) => t.type === service);
      if (exists) {
        toast.success('Service already added');
        return;
      }
      try {
        setSavingService(service);
        await api.post(`/tasks/company/${selected.id}`, {
          type: service,
          status: 'Not Started',
          branchId: Number(branchId),
        });
        await refreshTasks();
        toast.success('Branch service added');
      } catch (e) {
        console.error('add branch service', e);
        toast.error('Failed to add service');
      } finally {
        setSavingService('');
      }
    } else {
      // Remove service - show confirmation modal
      setShowRemoveConfirm(service);
    }
  };

  const handleConfirmRemoveService = async () => {
    const service = showRemoveConfirm;
    setShowRemoveConfirm(null);
    
    const task = tasks.find((t) => t.type === service);
    if (!task) {
      toast.error('Task not found');
      return;
    }

    // Warn if task has progress
    if (task.status !== 'Not Started') {
      const confirmed = window.confirm(
        `This service has status "${task.status}". Are you sure you want to remove it? Progress will be lost.`
      );
      if (!confirmed) return;
    }

    try {
      setSavingService(service);
      await api.delete(`/tasks/${task.id}`);
      await refreshTasks();
      toast.success('Service removed');
    } catch (e) {
      console.error('remove service', e);
      toast.error('Failed to remove service');
    } finally {
      setSavingService('');
    }
  };

  if (!selected) return null;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <AnimatedBackground />
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-white text-lg">Loading branch details...</div>
        </div>
      </div>
    );
  }

  const branch = selected.Branches?.find((b) => b.id === parseInt(branchId));
  const perBranch = selected.serverLocation === 'Per Branch';

  // Split devices into network vs other
  const netCount = branchInfo?.numberOfNetworkDevices || 0;
  const netDevices = devices.slice(0, netCount);
  const otherDevices = devices.slice(netCount);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <AnimatedBackground />
      
      <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            {branch?.name || `Branch ${branchId}`}
          </h1>
          <p className="text-slate-400">Complete branch onboarding tasks</p>
        </div>

        <ProgressSummary
          branchId={parseInt(branchId)}
          sectorId={selected?.sectorId}
          allowedUserFieldKeys={allowedUserQuickFieldKeys}
        />

        <ModernCard className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Branch Services</h3>
            <div className="flex items-center gap-3">
              {!isEditingServices ? (
                <>
                  <span className="text-slate-400 text-sm">Services configured for this branch</span>
                  {canEditServices && (
                    <button
                      onClick={() => setIsEditingServices(true)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Edit Services
                    </button>
                  )}
                </>
              ) : (
                <>
                  <span className="text-slate-400 text-sm">Click to add or remove services</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditingServices(false)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Read-Only Badge List View */}
          {!isEditingServices && (
            <div className="space-y-3">
              {activeServices.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p className="text-lg mb-2">No services configured for this branch</p>
                  {canEditServices && (
                    <p className="text-sm">Click "Edit Services" to add services</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                {activeServices.map((svc) => {
                  return (
                    <div
                      key={svc}
                      className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-500/60 dark:border-emerald-500/40 rounded-lg"
                    >
                      <span className="text-emerald-600 dark:text-emerald-400 text-lg">✓</span>
                      <span className="text-emerald-800 dark:text-emerald-100 font-medium">{svc}</span>
                    </div>
                  );
                })}
                </div>
              )}
            </div>
          )}

          {/* Edit Mode Grid View */}
          {isEditingServices && (
            <div className="grid sm:grid-cols-2 gap-3">
              {BRANCH_SERVICES.map((svc) => {
                const task = tasks.find((t) => t.type === svc);
                const exists = !!task;
                const isSaving = savingService === svc;
                
                return (
                  <button
                    key={svc}
                    onClick={() => handleToggleService(svc, !exists)}
                    disabled={isSaving}
                    className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border-2 transition ${
                      exists
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500 text-emerald-700 dark:text-emerald-100 hover:bg-emerald-100 dark:hover:bg-emerald-800/40'
                        : 'bg-gray-50 dark:bg-red-900/20 border-gray-300 dark:border-red-500/40 text-gray-600 dark:text-red-200 hover:bg-gray-100 dark:hover:bg-red-900/30 dark:hover:border-red-500/60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{exists ? '✓' : '✕'}</span>
                      <span className="font-medium">{svc}</span>
                    </div>
                    {isSaving && (
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        {exists ? 'Removing...' : 'Adding...'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Confirmation Modal */}
          {showRemoveConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-slate-800 rounded-xl p-6 max-w-md mx-4 border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">Remove Service?</h3>
                <p className="text-slate-300 mb-4">
                  Are you sure you want to remove <span className="font-semibold">{showRemoveConfirm}</span>?
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowRemoveConfirm(null)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmRemoveService}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}
        </ModernCard>

        {perBranch && (
          <ServerSection
            branchId={parseInt(branchId)}
            companyId={selected?.id}
            onServerChange={() => {
              // Refresh branch info to update server count
              api.get(`/branches/${branchId}`).then(res => {
                setBranchInfo(res.data);
                setSelected(prev => ({
                  ...prev,
                  Branches: prev.Branches?.map(b => b.id === res.data.id ? res.data : b)
                }));
              });
            }}
          />
        )}

        <DynamicEntitySection
          entityTypeCode="branch_user"
          sectorId={selected?.sectorId}
          branchId={parseInt(branchId)}
          companyId={selected?.id}
          title="User Onboarding"
          icon="👥"
          allowedQuickFieldKeys={allowedUserQuickFieldKeys}
        />

        <DynamicEntitySection
          entityTypeCode="branch_device"
          sectorId={selected?.sectorId}
          branchId={parseInt(branchId)}
          companyId={selected?.id}
          title="Device Onboarding"
          icon="💻"
        />

        <DynamicEntitySection
          entityTypeCode="firewall"
          sectorId={selected?.sectorId}
          branchId={parseInt(branchId)}
          companyId={selected?.id}
          title="Firewall"
          icon="🛡️"
        />

        {dynamicEntityTypes
          .filter((t) => !['branch_user', 'branch_device', 'firewall'].includes(t.code))
          .map((entityType) => (
            <DynamicEntitySection
              key={entityType.id}
              entityTypeCode={entityType.code}
              sectorId={selected?.sectorId}
              branchId={parseInt(branchId)}
              companyId={selected?.id}
              title={entityType.name}
              icon={entityType.icon}
            />
          ))}

        <BranchNotes
          notes={branchNotes}
          onAddNote={handleAddNote}
        />
      </div>
    </div>
  );
};

export default OnboardingBranchDetails;

